import React, { useEffect } from 'react';
import SettingsCard from '../../../components/structure/SettingsCard';
import UpdateOrganizationForm from './UpdateOrganizationForm';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import TemplateConfigurationContainer from '../../../state/containers/TemplateConfigurationContainer';
import MediaContainer from '../../../state/containers/MediaContainer';
import SparkContainer from '../../../hooks/SparkContainer';
import { aiAnsweringCommonAppName, aiAnsweringCustomModuleName, aiDentalSchedulingCommonAppName, aiDentalSchedulingCustomModuleName } from '../../../constants/sparkConstants/moduleConsts';
import TemplateConfigurationModel from '../../../models/templating/api/TemplateConfigurationModel';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import UpdateApplicationRequest from '../../../models/applications/api/UpdateApplicationRequest';
import { SparkOrganizationClientNotesCard } from '../../../components/clientContext/SparkClientContextNotesCard';

interface OrganizationInfoSettingsProps {
    orgContainer: OrganizationContainer
    mediaContainer: MediaContainer
    templateConfigurationContainer?: TemplateConfigurationContainer
    commonApp?: ApplicationModel
    appContainer?: ApplicationContainer
}

const OrganizationInfoSettings: React.FC<OrganizationInfoSettingsProps> = (props) => {

    const sparkContainer = SparkContainer.useContainer();

    useEffect(() => {
        if (sparkContainer.inSpark) {
            updateCommonAppNames();
        };
    }, [props.commonApp]);

    const updateCommonAppNames = async () => {

        if (props.commonApp) {

            const orgTemplateConfigs = await props.templateConfigurationContainer.getOrganizationTemplates(props.orgContainer?.state?.currentOrganization?.id);
            const commonAppTemplateConfigs = orgTemplateConfigs.data.filter(templateConfig => templateConfig.templateApplicationId === props.commonApp?.id);

            // Check if it's an AI answering app that's already correctly configured
            if ((props.commonApp?.name === aiAnsweringCommonAppName) && (commonAppTemplateConfigs[0]?.name === aiAnsweringCustomModuleName)) {
                return;
            }

            // Check if it's a dental scheduling app that's already correctly configured
            if ((props.commonApp?.name === aiDentalSchedulingCommonAppName) && (commonAppTemplateConfigs[0]?.name === aiDentalSchedulingCustomModuleName)) {
                return;
            }

            // Determine the correct app name and module name based on the template configuration
            let targetAppName = aiAnsweringCommonAppName;
            let targetModuleName = aiAnsweringCustomModuleName;

            if (commonAppTemplateConfigs.length > 0) {
                // Check if this is a dental scheduling configuration
                if (commonAppTemplateConfigs[0].name === aiDentalSchedulingCustomModuleName ||
                    commonAppTemplateConfigs[0].description?.includes('Shared Configuration')) {
                    targetAppName = aiDentalSchedulingCommonAppName;
                    targetModuleName = aiDentalSchedulingCustomModuleName;
                }
            }

            // update common app name
            if (props.commonApp?.name !== targetAppName) {
                const updatedApplication: UpdateApplicationRequest = { ...props.commonApp, name: targetAppName };
                props.appContainer.updateApplication(props.commonApp?.id, updatedApplication);
            };

            // update common app template config/module name
            if (orgTemplateConfigs) {
                if (commonAppTemplateConfigs) {
                    if (commonAppTemplateConfigs.length === 1) {
                        if (commonAppTemplateConfigs[0].name !== targetModuleName) {
                            const commonAppUpdatedTemplateConfiguration: TemplateConfigurationModel = {
                                ...commonAppTemplateConfigs[0],
                                name: targetModuleName
                            }
                            await props.templateConfigurationContainer.updateTemplateConfiguration(commonAppUpdatedTemplateConfiguration);
                        }
                    }
                };
            };

        };

    };

    return (
        <>
            <SettingsCard title="Organization Information">
                <UpdateOrganizationForm orgContainer={props.orgContainer} mediaContainer={props.mediaContainer} />
            </SettingsCard>
            {sparkContainer.inSpark && (
                <SparkOrganizationClientNotesCard orgContainer={props.orgContainer} />
            )}
        </>
    )
}

export default OrganizationInfoSettings;
